import {
  HbrInput,
  HbrOption,
  HbrSelect,
  HbrSpinner,
  HbrSwitch,
  HbrTextarea,
} from '@harbor/elements-react-standalone';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { useFetchOpportunity } from '../../hooks/opportunity-hooks';
import { checkAndTagSE, createSEActivityForOpportunity } from '../../support/opportunityHelpers';
import { updateSENote } from '../../support/salesForceHelpers';
import { getSfOptions } from '../../support/slices/salesforceOptionsSlice';
import { RootState, useAppDispatch } from '../../support/store';
import { asSingleValue } from '../../support/utils';
import {
  OpportunityPhase,
  OpportunityPhaseLabel,
} from '../../types/opportunity';
import StbApiModal from '../security-toolbox-ui/StbApiModal';
import StbButton from '../security-toolbox-ui/StbButton';


const SEWorkLogger: React.FC = () => {
  const { id } = useParams();
  const { data: opportunity } = useFetchOpportunity(id);

  const [open, setOpen] = useState(false);
  const [summary, setSummary] = useState('');
  const [summaryTouched, setSummaryTouched] = useState(false);
  const [details, setDetails] = useState('');
  const [serviceType, setServiceType] = useState('');
  const [technology, setTechnology] = useState('');
  const [includeActivity, setIncludeActivity] = useState(false);

  const dispatch = useAppDispatch();

  useEffect(() => {
    dispatch(getSfOptions('SEServiceType'));
    dispatch(getSfOptions('Technology'));
  }, [dispatch]);
  const sfOptionsServiceType = useSelector(
    (state: RootState) => (state.salesforceOptions?.SEServiceType)
  );
  const sfOptionsTechnology = useSelector(
    (state: RootState) => (state.salesforceOptions?.Technology)
  )
  const sfOptions = sfOptionsServiceType?.options?.filter((option) => option.value !== 'POV') ?? [];
  const sfTechnologyOptions = sfOptionsTechnology?.options ?? [];
  const sfOptionsStatus = sfOptionsServiceType?.status ?? 'loading';
  const sfTechnologyOptionsStatus = sfOptionsTechnology?.status ?? 'loading';
  const sfOptionsLoading = sfOptionsStatus === 'loading' || sfTechnologyOptionsStatus === 'loading';

  const handleOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setSummary('');
    setSummaryTouched(false);
    setDetails('');
    setServiceType('');
    setIncludeActivity(false);
  };

  const handleSubmit = async () => {
    try {
      const summaryStr = (opportunity?.se_phase
        ? `${OpportunityPhaseLabel[opportunity.se_phase]} - ${summary}`
        : `${OpportunityPhaseLabel[OpportunityPhase.Discovery]} - ${summary}`);
      const detailsStr = details + (includeActivity ? ' (SE Activity generated)' : '')
      if (includeActivity) {
        await checkAndTagSE(opportunity);
        await createSEActivityForOpportunity(opportunity, {
          summary: summaryStr,
          details: detailsStr,
          serviceType,
          technology,
        });
      }
      await updateSENote(opportunity, `${summaryStr} - ${detailsStr}`);
    } catch (error) {
      console.error('Error logging work:', error);
      throw error;
    }
  };

  return (
    <div>
      <StbButton onClick={handleOpen} className="hbr-button-group__button hbr-button-group__button--inner">Log Work</StbButton>
      <StbApiModal
        title="Log Activity"
        open={open}
        role="save"
        submitLabel="Submit Log"
        submitDisabled={(summary ?? '').trim() === '' || (includeActivity ? serviceType === '' : false)}
        submitSuccess="Activity logged successfully"
        submitError="Failed to log activity"
        onClose={async () => await handleClose()}
        onSubmit={handleSubmit}
      >
        <HbrInput
          type='text'
          value={summary}
          onHbr-input={(e) => {
            setSummary(e.target.value);
            setSummaryTouched(true);
          }}
          error={summaryTouched && (summary ?? '') === ''}
        >
          <div slot="label">Summary <em className="text-gray-500">(required)</em></div>
          {summaryTouched && (summary ?? '') === '' && (
            <div slot="help-text" className="text-sm mt-1 text-danger">
              Summary is required
            </div>
          )}
        </HbrInput>
        <HbrTextarea
          value={details ?? ''}
          onHbr-input={(e: any) => setDetails(e?.target?.value ?? '')}
          label="Details"
        />
        <div>
          <div className='flex items-center gap-1'>
            <HbrSwitch
              size='small'
              checked={includeActivity}
              onHbr-click={() => setIncludeActivity(!includeActivity)}
              className='flex items-center'
            />
            <label className='text-sm font-semibold text-[#23282e] mb-3 pt-1'>
              Create an SE Activity for this log
            </label>
          </div>
          {includeActivity && (
            <div>
              {sfOptionsStatus === 'success' && (
                <HbrSelect
                  placeholder="Select Type"
                  onHbr-change={(evt) => setServiceType(asSingleValue(evt.target.value))}
                >
                  {(sfOptions ?? []).map((o, n) => (
                    <HbrOption value={o.value} key={n + o.value}>
                      {o.label}
                    </HbrOption>
                  ))}
                  <div slot="label">SE Service Type <em className="text-gray-500">(required)</em></div>
                </HbrSelect>
              )}
              {sfTechnologyOptionsStatus === 'success' && (
                <HbrSelect
                  placeholder="Select Type"
                  onHbr-change={(evt) => setTechnology(asSingleValue(evt.target.value))}
                >
                  {(sfTechnologyOptions ?? []).map((o, n) => (
                    <HbrOption value={o.value} key={n + o.value}>
                      {o.label}
                    </HbrOption>
                  ))}
                  <div slot="label">Technology <em className="text-gray-500">(required)</em></div>
                </HbrSelect>
              )}
              {sfOptionsLoading && <HbrSpinner size='small' />}
            </div>
          )}
        </div>
      </StbApiModal>
    </div>
  );
};

export default SEWorkLogger;
